/**
 * Built-in pack templates for Smart Plans.
 *
 * These are deterministic, locale-aware templates that ship with the app.
 * They are seeded into the `plan` table on first use (or lazily on demand).
 *
 * Convention: Each pack has a stable ID prefixed with "builtin:" so they can
 * be upserted idempotently.
 */

import { AnchorDateType, AutomationTrigger } from "../planTypes.js"
import type { DbPack } from "../planTypes.js"

// ---------------------------------------------------------------------------
// Birthday Dinner
// ---------------------------------------------------------------------------

export const PACK_BIRTHDAY: DbPack = {
  id: "builtin:birthday",
  kind: "pack",
  name: "Birthday Celebration",
  description: "Plan a birthday — gift, invitations, food, and the big day.",
  icon: "pika:general/gift-default",
  category: "events",
  triggers: ["birthday", "födelsedag", "bday"],
  anchorDateType: AnchorDateType.Event,
  version: 1,
  isBuiltIn: true,
  isPublished: true,

  questions: [
    {
      key: "name",
      label: "Whose birthday?",
      type: "text",
      required: true,
    },
    {
      key: "party_type",
      label: "What kind of celebration?",
      type: "select",
      options: [
        { value: "dinner_out", label: "Dinner out" },
        { value: "dinner_home", label: "Dinner at home" },
        { value: "party", label: "Party" },
        { value: "simple", label: "Just acknowledge" },
      ],
      default: "dinner_out",
    },
    {
      key: "has_cake",
      label: "Order a cake?",
      type: "boolean",
      default: true,
    },
    {
      key: "guest_count",
      label: "Approximate number of guests?",
      type: "number",
      condition: "answers.party_type !== 'simple'",
      default: 6,
    },
  ],

  steps: [
    {
      stepKey: "buy_gift",
      titleTemplate: "Buy gift for {{name}}",
      noteTemplate: "Think about what {{name}} likes. Set a budget.",
      offsetRule: "-30d>sat",
      sequenceOrder: 1,
      tags: ["shopping"],
      estimatedMinutes: 60,
      reminderOffset: "-2d 09:00",
      checklistItems: [
        "Brainstorm ideas",
        "Set budget",
        "Buy gift",
        "Wrap gift",
      ],
    },
    {
      stepKey: "send_invitations",
      titleTemplate: "Send invitations for {{name}}'s birthday",
      offsetRule: "-21d",
      sequenceOrder: 2,
      condition: "answers.party_type !== 'simple'",
      checklistItems: [
        "Create guest list",
        "Send invitations",
        "Collect RSVPs",
      ],
    },
    {
      stepKey: "book_restaurant",
      titleTemplate: "Book restaurant for {{name}}'s birthday",
      noteTemplate: "Number of guests: {{guest_count}}",
      offsetRule: "-14d~wd",
      sequenceOrder: 3,
      condition: "answers.party_type === 'dinner_out'",
      estimatedMinutes: 15,
      tags: ["booking"],
    },
    {
      stepKey: "plan_menu",
      titleTemplate: "Plan menu for {{name}}'s birthday dinner",
      offsetRule: "-10d",
      sequenceOrder: 3,
      condition: "answers.party_type === 'dinner_home'",
      checklistItems: [
        "Choose starter",
        "Choose main course",
        "Choose dessert",
        "Buy ingredients",
      ],
    },
    {
      stepKey: "order_cake",
      titleTemplate: "Order birthday cake for {{name}}",
      offsetRule: "-7d~wd",
      sequenceOrder: 4,
      condition: "answers.has_cake === true",
      estimatedMinutes: 15,
      tags: ["shopping"],
    },
    {
      stepKey: "buy_decorations",
      titleTemplate: "Buy decorations for the party",
      offsetRule: "-3d>sat",
      sequenceOrder: 5,
      condition: "answers.party_type === 'party'",
      optional: true,
      estimatedMinutes: 30,
      tags: ["shopping"],
    },
    {
      stepKey: "buy_flowers",
      titleTemplate: "Buy flowers for {{name}}",
      offsetRule: "-1d",
      sequenceOrder: 6,
      estimatedMinutes: 20,
      tags: ["shopping"],
    },
    {
      stepKey: "morning_call",
      titleTemplate: "Call and wish {{name}} happy birthday",
      offsetRule: "same_day 07:00",
      sequenceOrder: 7,
      reminderOffset: "same_day 07:00",
    },
  ],

  automations: [
    {
      trigger: AutomationTrigger.AnchorDateChanged,
      action: { type: "reschedule_all", params: { notification: true } },
    },
    {
      trigger: AutomationTrigger.AllStepsCompleted,
      action: { type: "complete_plan" },
    },
  ],
}

// ---------------------------------------------------------------------------
// Trip Planning
// ---------------------------------------------------------------------------

export const PACK_TRIP: DbPack = {
  id: "builtin:trip",
  kind: "pack",
  name: "Trip Planning",
  description: "Plan a trip — booking, packing, logistics, and travel day.",
  icon: "pika:automotive/airplane-default",
  category: "travel",
  triggers: ["trip", "resa", "travel", "vacation", "semester"],
  anchorDateType: AnchorDateType.Event,
  version: 1,
  isBuiltIn: true,
  isPublished: true,

  questions: [
    {
      key: "destination",
      label: "Where are you going?",
      type: "text",
      required: true,
    },
    {
      key: "needs_visa",
      label: "Do you need a visa?",
      type: "boolean",
      default: false,
    },
    {
      key: "has_pets",
      label: "Do you have pets that need care?",
      type: "boolean",
      default: false,
    },
  ],

  steps: [
    {
      stepKey: "apply_visa",
      titleTemplate: "Apply for visa to {{destination}}",
      offsetRule: "-60d~wd",
      sequenceOrder: 1,
      condition: "answers.needs_visa === true",
      estimatedMinutes: 120,
      tags: ["admin"],
      checklistItems: [
        "Check visa requirements",
        "Gather documents",
        "Submit application",
        "Track status",
      ],
    },
    {
      stepKey: "book_flights",
      titleTemplate: "Book flights to {{destination}}",
      offsetRule: "-45d",
      sequenceOrder: 2,
      estimatedMinutes: 30,
      tags: ["booking"],
      reminderOffset: "-2d 09:00",
    },
    {
      stepKey: "book_accommodation",
      titleTemplate: "Book accommodation in {{destination}}",
      offsetRule: "-40d",
      sequenceOrder: 3,
      estimatedMinutes: 45,
      tags: ["booking"],
    },
    {
      stepKey: "travel_insurance",
      titleTemplate: "Arrange travel insurance",
      offsetRule: "-30d~wd",
      sequenceOrder: 4,
      estimatedMinutes: 20,
      tags: ["admin"],
    },
    {
      stepKey: "arrange_pet_care",
      titleTemplate: "Arrange pet care while away",
      offsetRule: "-14d",
      sequenceOrder: 5,
      condition: "answers.has_pets === true",
      estimatedMinutes: 15,
    },
    {
      stepKey: "create_packing_list",
      titleTemplate: "Create packing list for {{destination}}",
      offsetRule: "-7d",
      sequenceOrder: 6,
      checklistItems: [
        "Clothes",
        "Toiletries",
        "Electronics & chargers",
        "Travel documents",
        "Medications",
      ],
    },
    {
      stepKey: "pack_bags",
      titleTemplate: "Pack bags for {{destination}}",
      offsetRule: "-1d",
      sequenceOrder: 7,
      estimatedMinutes: 60,
      reminderOffset: "-1d 18:00",
    },
    {
      stepKey: "travel_day",
      titleTemplate: "Travel day — {{destination}}!",
      offsetRule: "same_day 06:00",
      sequenceOrder: 8,
      checklistItems: [
        "Check in online",
        "Confirm transport to airport",
        "Verify passport & documents",
      ],
    },
  ],

  automations: [
    {
      trigger: AutomationTrigger.AnchorDateChanged,
      action: { type: "reschedule_all", params: { notification: true } },
    },
    {
      trigger: AutomationTrigger.AllStepsCompleted,
      action: { type: "complete_plan" },
    },
  ],
}

// ---------------------------------------------------------------------------
// Moving
// ---------------------------------------------------------------------------

export const PACK_MOVING: DbPack = {
  id: "builtin:moving",
  kind: "pack",
  name: "Moving",
  description: "Plan a move — logistics, admin, packing, and moving day.",
  icon: "pika:building/home-default",
  category: "life",
  triggers: ["move", "flytta", "moving", "flytt"],
  anchorDateType: AnchorDateType.Event,
  version: 1,
  isBuiltIn: true,
  isPublished: true,

  questions: [
    {
      key: "new_address",
      label: "What's the new address?",
      type: "text",
      required: true,
    },
    {
      key: "hiring_movers",
      label: "Are you hiring professional movers?",
      type: "boolean",
      default: true,
    },
  ],

  steps: [
    {
      stepKey: "book_movers",
      titleTemplate: "Book moving company",
      noteTemplate: "Moving to: {{new_address}}",
      offsetRule: "-45d~wd",
      sequenceOrder: 1,
      condition: "answers.hiring_movers === true",
      estimatedMinutes: 30,
      tags: ["booking"],
      reminderOffset: "-2d 09:00",
    },
    {
      stepKey: "rent_truck",
      titleTemplate: "Rent moving truck",
      noteTemplate: "Moving to: {{new_address}}",
      offsetRule: "-30d",
      sequenceOrder: 1,
      condition: "answers.hiring_movers === false",
      estimatedMinutes: 20,
      tags: ["booking"],
    },
    {
      stepKey: "declutter",
      titleTemplate: "Declutter and donate/sell unwanted items",
      offsetRule: "-30d>sat",
      sequenceOrder: 2,
      estimatedMinutes: 180,
      checklistItems: [
        "Go through closets",
        "Sort kitchen items",
        "Donate or sell excess",
      ],
    },
    {
      stepKey: "change_address",
      titleTemplate: "Update address with authorities & services",
      offsetRule: "-21d~wd",
      sequenceOrder: 3,
      tags: ["admin"],
      checklistItems: [
        "Post office / mail forwarding",
        "Bank & insurance",
        "Employer / school",
        "Subscriptions & deliveries",
        "Government / tax office",
      ],
    },
    {
      stepKey: "get_packing_supplies",
      titleTemplate: "Get packing supplies (boxes, tape, markers)",
      offsetRule: "-14d>sat",
      sequenceOrder: 4,
      estimatedMinutes: 45,
      tags: ["shopping"],
    },
    {
      stepKey: "pack_non_essentials",
      titleTemplate: "Pack non-essential items",
      offsetRule: "-10d",
      sequenceOrder: 5,
      estimatedMinutes: 240,
      checklistItems: [
        "Books & decorations",
        "Off-season clothes",
        "Hobby supplies",
        "Label all boxes",
      ],
    },
    {
      stepKey: "cancel_utilities_old",
      titleTemplate: "Cancel utilities at old address",
      offsetRule: "-7d~wd",
      sequenceOrder: 6,
      tags: ["admin"],
      checklistItems: [
        "Electricity",
        "Internet",
        "Water / heating",
      ],
    },
    {
      stepKey: "setup_utilities_new",
      titleTemplate: "Set up utilities at new address",
      noteTemplate: "Address: {{new_address}}",
      offsetRule: "-7d~wd",
      sequenceOrder: 6,
      tags: ["admin"],
      checklistItems: [
        "Electricity",
        "Internet",
        "Water / heating",
      ],
    },
    {
      stepKey: "pack_essentials",
      titleTemplate: "Pack remaining essentials (last box)",
      offsetRule: "-1d",
      sequenceOrder: 7,
      estimatedMinutes: 120,
      checklistItems: [
        "Toiletries",
        "Phone chargers",
        "Change of clothes",
        "Important documents",
        "Snacks & water",
      ],
    },
    {
      stepKey: "moving_day",
      titleTemplate: "Moving day!",
      noteTemplate: "New address: {{new_address}}",
      offsetRule: "same_day 07:00",
      sequenceOrder: 8,
      reminderOffset: "same_day 06:00",
      checklistItems: [
        "Final walkthrough of old place",
        "Hand over keys",
        "Supervise loading/unloading",
        "Check nothing left behind",
      ],
    },
  ],

  automations: [
    {
      trigger: AutomationTrigger.AnchorDateChanged,
      action: { type: "reschedule_all", params: { notification: true } },
    },
    {
      trigger: AutomationTrigger.AllStepsCompleted,
      action: { type: "complete_plan" },
    },
  ],
}

// ---------------------------------------------------------------------------
// Registry — all built-in packs
// ---------------------------------------------------------------------------

export const BUILT_IN_PACKS: readonly DbPack[] = [
  PACK_BIRTHDAY,
  PACK_TRIP,
  PACK_MOVING,
] as const

/**
 * Find matching built-in packs by a trigger keyword.
 * Case-insensitive partial match on pack triggers.
 */
export function matchBuiltInPacks(keyword: string): DbPack[] {
  const lower = keyword.toLowerCase()
  return BUILT_IN_PACKS.filter(
    (pack) => pack.triggers?.some((t) => t.includes(lower) || lower.includes(t)),
  )
}
